import GD from '../GD'
import { WSMessage } from '../enums'
import { DCCAPI, Services } from '../utils'
import { CHUNK_UPLOAD_TRIGGER_SIZE } from '../enums'
import { Config } from './index'

class FileTransfer {
  private key: string | undefined
  private uploadQueue: UploadingFileVO[] = []
  private isProcessingQueue = false
  private processingDelay = false
  private uploadingFiles: Map<string, UploadingFileVO> = new Map()

  constructor() {
    GD.AUTH_COMPLETE.subscribe(async (data) => {
      this.key = data.key
    })

    GD.REQ_FILE_UPLOAD.listener = async (request): Promise<boolean> => {
      const isNotGuest = this.key !== 'web'

      const shouldUseChunks = isNotGuest && request.file.size > CHUNK_UPLOAD_TRIGGER_SIZE // 1.5mb

      if (shouldUseChunks) {
        return Services.chunkUploader.startUploadViaChunks(request)
      }
      else {
        return this.startUploadViaForm(request)
      }
    }

    GD.FILE_DOWNLOAD_REQUEST.subscribe(async (req) => {
      const url = window.DCC_CONFIG.API_FILES_URL + 'uid=' + req.uid + '&key=' + this.key
      const link = document.createElement('a')
      link.setAttribute('download', req.name)
      link.href = url
      document.body.appendChild(link)
      link.click()
      link.remove()
    })

    GD.SEND_FILES.subscribe(async (fileList: File[]) => {
      const chat: ChatVO = await GD.GET_CHAT.request()

      this.uploadingFiles.clear()

      for (let i = 0; i < fileList.length; i++) {
        const tempUID: string = btoa(Math.random() * 10000 + '_' + +new Date())
        const file = fileList[i]
        if (!file) continue
        const fileObj: UploadingFileVO = {
          file: file,
          tempUID: tempUID,
          chatUID: chat.uid,
          status: 'start',
          started: +new Date(),
        }

        this.uploadingFiles.set(tempUID, fileObj)
      }

      const files = this.uploadingFiles

      for (const [tempUID, fileData] of files) {
        this.uploadingFiles.set(tempUID, fileData)
        this.uploadQueue.push(fileData)
      }

      GD.FILE_UPLOADING_LIST_UPDATED.invoke(Array.from(this.uploadingFiles.values()))
      // Start processing uploads
      await this.processUploadQueue()
    })

    GD.FILE_PROGRESS_DELAY.subscribe((packet) => (this.processingDelay = packet.delay))

    GD.SERVICE_READY.invoke('fileTransfer')
  }

  private async startUploadViaForm(req: { file: File, owner_uid?: string, tempUID: string }): Promise<boolean> {
    const chat = await GD.GET_CHAT.request()

    GD.FILE_PROGRESS_UPDATE.invoke({ tempUID: req.tempUID, progress: 50 })

    const resp = await GD.REQ_HTTP.request({
      url: window.DCC_CONFIG.API_FILES_URL,
      method: 'files.addDoc',
      chatUID: chat.uid,
      doc: req.file,
      title: req.file.name,
      encrypt: false,
    })

    if (!resp || resp.error || !resp.data) {
      if (resp.errorMsg) GD.LOGE.invoke(resp.errorMsg)
      return false
    }

    GD.WS_SEND.invoke({
      method: WSMessage.MESSAGE_ADD,
      data: {
        chatUID: chat.uid,
        text:
          DCCAPI.pack(
            Config.systemSequence +
            JSON.stringify({
              method: 'fileSended',
              type: 'file',
              title: req.file.name,
              fileType: 'file',
              fileData: resp.data,
              additionalData: (resp.data as IObjectData).uid,
            }),
            chat.securityKey,
          )
      },
    })

    return true
  }

  private async processUploadQueue() {
    if (this.isProcessingQueue || this.uploadQueue.length === 0) return

    // --------- //
    this.isProcessingQueue = true
    const fileData = this.uploadQueue.shift() // Get the next file from the queue and remove task from queue
    if (!fileData) return

    const uploadResp = await this.uploadFile(fileData.file, fileData.tempUID)
    this.isProcessingQueue = false
    // --------- //
    if (this.processingDelay) await this.pauseUploadQueue() // check if fullsize img is not uploading
    if (!this.processingDelay) await this.onFileUploaded(fileData.tempUID, uploadResp) // update upload view
    if (this.uploadQueue.length) await this.processUploadQueue() // Repeat recursively
  }

  private async pauseUploadQueue() {
    await new Promise<void>((resolve) => {
      const intervalId = setInterval(() => {
        if (!this.processingDelay) {
          clearInterval(intervalId)
          resolve()
        }
      }, 1000)
    })
  }

  private async uploadFile(file: File | null, tempUID: string) {
    if (!file) return

    const uploadRequest = this.isImage(file) ? GD.UPLOAD_IMAGE : GD.REQ_FILE_UPLOAD

    return await uploadRequest.request({
      tempUID,
      file,
    })
  }

  private isImage(file: File): boolean {
    if (file.type.indexOf('/image') !== -1) return false

    return (
      file.type.indexOf('jpeg') !== -1 ||
      file.type.indexOf('jpg') !== -1 ||
      file.type.indexOf('png') !== -1 ||
      file.type.indexOf('gif') !== -1
    )
  }

  private async onFileUploaded(tempUID: string, isSuccess: boolean | undefined) {
    const uploadFile = this.uploadingFiles.get(tempUID)
    if (!uploadFile) return

    uploadFile.status = isSuccess ? 'finish' : 'fail'

    GD.FILE_UPLOADING_LIST_UPDATED.invoke(Array.from(this.uploadingFiles.values()))
  }
}

export default FileTransfer